import React, { memo, useState, useCallback, useEffect } from "react";
import OrganizationSelectView from "./view";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  selectedOrganizationAtom,
  organizationsAtom,
  snackbarAtom,
} from "../../selectorsAtoms";
import {
  useGetOrganizations,
  useLazyOrganizationsMutation,
} from "../../mutationsQueries/organization";
import { OrganizationInterface } from "../../types";
import { useDebounce } from "react-use";
// @ts-ignore
import { usePrevious } from "react-hookedup";
import GlobalSharedStateClass from "../../util/globalSharedStateClass";
import isEqual from "react-fast-compare";

const OrganizationSelect = () => {
  const CacheClass = new GlobalSharedStateClass();
  const [searchValue, setSearchValue] = useState("");
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState(
    CacheClass.getOrganizationSort() || "id"
  );
  const [organizations, setOrganizations] = useRecoilState(organizationsAtom);
  const [selectedOrganizations, setSelectedOrganizations] = useRecoilState<any>(
    selectedOrganizationAtom
  );
  const setSnackbar = useSetRecoilState(snackbarAtom);
  const open = Boolean(anchorEl);
  useGetOrganizations();
  const { error, data: organizationData }: any = useGetOrganizations(open);
  const lazyOrganizationMutation = useLazyOrganizationsMutation();
  const previousOrganizations = usePrevious(organizationData);
  const previousOffset = usePrevious(offset);

  useEffect(() => {
    if (offset && previousOffset !== offset) {
      const additionalParams = searchValue ? { search: searchValue } : {};
      lazyOrganizationMutation.mutate({
        offset: offset * 20,
        ...additionalParams,
      });
    }
  }, [offset, previousOffset, lazyOrganizationMutation, searchValue]);

  useDebounce(
    () => {
      if (searchValue) {
        lazyOrganizationMutation.mutate({ search: searchValue });
      }
    },
    300,
    [searchValue, organizationData]
  );

  const sortListByParam = useCallback(
    (a: { name: string; id: number }, b: { name: string; id: number }) => {
      if (sortBy === "name" || sortBy === "id") {
        return a[sortBy] > b[sortBy] ? 1 : -1;
      } else if (sortBy === "selected") {
        const aItem = selectedOrganizations.find(
          (item: { id: number }) => item.id === a.id
        );
        const bItem = selectedOrganizations.find(
          (item: { id: number }) => item.id === b.id
        );

        if (aItem) {
          if (bItem) {
            return a.id > b.id ? 1 : -1;
          }
          return -1;
        } else if (bItem) {
          if (aItem) {
            return a.id > b.id ? -1 : 1;
          }
          return 1;
        }
      }
    },
    [sortBy, selectedOrganizations]
  );

  useEffect(() => {
    if (!previousOrganizations && organizationData) {
      let cachedOrganization = CacheClass.getCacheSelectedOrganizations();
      const sortedList = organizationData?.data
        ?.slice()
        .sort((a: { name: string }, b: { name: string }) =>
          a?.name > b?.name ? 1 : -1
        );

      if (sortedList?.length) {
        if (!cachedOrganization)
          CacheClass.setCacheSelectedOrganizations([sortedList[0]]);
        if (cachedOrganization || (sortedList.length && sortedList[0])) {
          setSelectedOrganizations(cachedOrganization || [sortedList[0]]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData, previousOrganizations]);

  useEffect(() => {
    if (organizationData) {
      setOrganizations(organizationData);
    }
  }, [organizationData, setOrganizations]);

  useEffect(() => {
    if (error) setSnackbar([{ open: true, message: error.message }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleClose = useCallback(() => {
    setSearchValue("");
    setAnchorEl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deselectAllOrganizations = useCallback(() => {
    setSelectedOrganizations([]);
    CacheClass.setCacheSelectedOrganizations([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    []
  );

  const onSelect = useCallback(
    (id: number | string) => {
      const foundOrganization = organizations.data.find(
        (item: OrganizationInterface) => item.id === id
      );
      let organizationsList = undefined;

      setSelectedOrganizations(
        (selectedOrganizations: OrganizationInterface[]) => {
          organizationsList = selectedOrganizations.find(
            (item: { id: number }) => item.id === id
          )
            ? selectedOrganizations.filter(
                (item: { id: number }) => item.id !== id
              )
            : [...selectedOrganizations, foundOrganization];
          return organizationsList;
        }
      );

      CacheClass.setCacheSelectedOrganizations(organizationsList);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [organizations, searchValue]
  );

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value),
    []
  );

  const searchFilter = useCallback(
    (item: OrganizationInterface) => {
      if (!searchValue) return true;
      return (
        item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        item.id.toString() === searchValue.toLowerCase()
      );
    },
    [searchValue]
  );

  const onScroll = useCallback(
    () => setOffset(offset => offset + 1),
    [setOffset]
  );

  return (
    <OrganizationSelectView
      open={open}
      isLoading={lazyOrganizationMutation.isLoading}
      organizationData={organizations}
      selectedOrganizations={selectedOrganizations}
      anchorEl={anchorEl}
      sortBy={sortBy}
      searchValue={searchValue}
      searchFilter={searchFilter}
      onSearchChange={onSearchChange}
      handleClose={handleClose}
      handleClick={handleClick}
      onSelect={onSelect}
      onScroll={onScroll}
      setSortBy={setSortBy}
      deselectAllOrganizations={deselectAllOrganizations}
      sortListByParam={sortListByParam}
    />
  );
};

export default memo(OrganizationSelect, isEqual);
